import { twMerge } from "tailwind-merge";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { useMediaQuery } from "usehooks-ts";
import { useCustomerZonesList } from "../../hooks/api/v2/customerZones/useCustomerZonesList";
import { Anchor, Button, Card, Icon, IconType } from "@sellar-io/components";
import { useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { CustomerZoneListResponseType } from "@sellar-io/api-v2/types";
import { useUniqueDirectPostcodes } from "./useUniqueDirectPostcodes";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { SpinnerLegacy } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { useCustomerZonesCreate } from "../../hooks/api/v2/customerZones/useCustomerZonesCreate";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { StorefrontPreviewer } from "../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { capitalize } from "@sellar-io/common";
import { useEffect } from "react";
import { useSellarDistributionAvailabilityDeliveryPostcodeList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDeliveryPostcodeList";

export const CustomerZoneLists = () => {
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );

  const sellarDistributionSetupFlag = useRemoteFeatureFlags().isActive(
    "sellarDistributionSetup",
  );

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const {
    data: customerZonesData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchNextPageError,
    isSuccess,
  } = useCustomerZonesList({
    sortBy: "type",
    limit: 100,
  });

  useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchNextPageError) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching, isFetchNextPageError]);

  const hasLoadedAllCustomerZones = isSuccess && !hasNextPage;

  const customerZones = customerZonesData?.pages.flatMap((p) => p) ?? [];

  const hasLocalCustomerZone = customerZones.some(
    (cz) => cz.type === "direct" && cz.name?.toLowerCase() === "local",
  );

  const hasNationwideCustomerZone = customerZones.some(
    (cz) => cz.default && cz.type === "thirdParty",
  );

  return (
    <>
      <NavigationBarMenu
        title="Customer Zones"
        borderBottom={false}
        rightComponent={
          isSupplierStorefrontFeatureFlagEnabled ? (
            <StorefrontPreviewButton />
          ) : null
        }
      />
      <div className="mx-auto flex max-w-[920px] items-center gap-2">
        <div
          className={twMerge(
            "flex w-full flex-col gap-4 px-4",
            isDesktop ? "mt-10" : "mt-2",
          )}
        >
          <Card className="flex flex-col gap-1 bg-white p-4">
            <span className="font-semibold">Customer Zones</span>
            <p>
              The Customer Zone your customers fall into decides what pricing
              and products they see when they come to your Storefront. You have
              two default Customer Zones - Local and Nationwide. For more
              information and help setting up your Customer Zones, visit our
              help centre{" "}
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/12011312-supplier-guide-customer-zones"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
              .
            </p>
          </Card>
          {sellarDistributionSetupFlag &&
            customerZones
              .filter((cz) => cz.systemDefinedType === "sellarDistribution")
              .map((customerZone) => (
                <SellarDistributionCustomerZoneItem
                  key={customerZone.id}
                  customerZone={customerZone}
                />
              ))}
          {hasLoadedAllCustomerZones && !hasLocalCustomerZone && (
            <SetupLocalZone />
          )}
          {customerZones
            .filter((cz) => !cz.systemDefinedType)
            .map((customerZone) => (
              <CustomerZoneItem
                key={customerZone.id}
                customerZone={customerZone}
              />
            ))}
          {hasLoadedAllCustomerZones && !hasNationwideCustomerZone && (
            <SetupNationwideZone />
          )}
        </div>
      </div>
    </>
  );
};

const SetupLocalZone = () => {
  const navigate = useNavigate();

  const uniqueDirectPostcodes = useUniqueDirectPostcodes();

  const customerZoneCreate = useCustomerZonesCreate();

  const deliveryAreas = useDeliveryAreaList({
    query: [],
    limit: 10,
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const showDeliveryAlert = !deliveryAreas.data?.pages[0]?.[0];

  const onClick = () => {
    customerZoneCreate.mutate(
      {
        type: "direct",
        name: "Local",
        default: false,
        PriceTierId: null,
        AvailabilityTierId: null,
        OrderRulesId: null,
      },
      {
        onSuccess: (response) => {
          navigate(ROUTES.CUSTOMER_ZONE.href({ customerZoneId: response.id }));
        },
      },
    );
  };

  return (
    <Card
      className="flex w-full cursor-pointer overflow-hidden border border-blue-300 bg-white hover:bg-zinc-50"
      onClick={() => {
        if (!showDeliveryAlert) {
          onClick();
        }
      }}
    >
      {deliveryAreas.isLoading ? (
        <SpinnerLegacy />
      ) : (
        <div className="flex w-full flex-col">
          {showDeliveryAlert ? (
            <Alert
              title={
                "Add your direct delivery areas to create a Local customer zone"
              }
              message={
                "Your Local customer zone is defined by the postcodes you visit on your direct delivery routes. Add them now to create a Local customer zone where your local customers can see the appropriate products and pricing on your storefront"
              }
              type={"information"}
              className="rounded-b-none border-0"
            >
              <Button
                color={"secondary"}
                className={
                  "border border-blue-300 bg-blue-100 text-blue-800 hover:bg-blue-200"
                }
                onClick={() =>
                  navigate(ROUTES.NEW_DELIVERY_AREA, {
                    state: { redirectToCustomerZones: true },
                  })
                }
              >
                Add Delivery Areas
              </Button>
            </Alert>
          ) : (
            <Alert
              title={"Setup your Local customer zone"}
              message={
                "Assign a price tier and product catalogue to your Local customer zone, so that customers inside your direct delivery areas will see the appropriate products and pricing on your storefront"
              }
              type={"information"}
              className="rounded-b-none border-0"
            />
          )}
          <div className="flex w-full flex-col gap-4 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col">
                <span className="font-semibold">Local</span>
                <span className="text-sm text-zinc-500">
                  {uniqueDirectPostcodes.hasPostcodes ? (
                    uniqueDirectPostcodes.textList
                  ) : (
                    <span className="flex items-center gap-2 text-blue-800">
                      <Icon icon="INFORMATION" />
                      No postcodes set
                    </span>
                  )}
                </span>
              </div>
              <span className="rounded-full bg-purple-800 px-2 py-1 text-sm text-white">
                Direct
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <TradeTerm term="Price tier" />
              <TradeTerm term="Product catalogue" />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const SetupNationwideZone = () => {
  const navigate = useNavigate();

  const customerZoneCreate = useCustomerZonesCreate();

  const onClick = () => {
    customerZoneCreate.mutate(
      {
        type: "thirdParty",
        name: "Nationwide",
        default: true,
        PriceTierId: null,
        AvailabilityTierId: null,
        OrderRulesId: null,
      },
      {
        onSuccess: (response) => {
          navigate(ROUTES.CUSTOMER_ZONE.href({ customerZoneId: response.id }));
        },
      },
    );
  };

  return (
    <Card
      className="flex w-full cursor-pointer overflow-hidden border border-blue-300 bg-white hover:bg-zinc-50"
      onClick={onClick}
    >
      <div className="flex w-full flex-col">
        <Alert
          title={"Setup your Nationwide customer zone"}
          message={
            "Assign a price tier and product catalogue to your Nationwide customer zone, so that customers outside your direct delivery areas will see the appropriate products and pricing on your storefront"
          }
          type={"information"}
          className="rounded-b-none border-0"
        />
        <div className="flex w-full flex-col gap-4 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col">
              <span className="font-semibold">Nationwide</span>
              <span className="flex items-center gap-2 text-sm text-zinc-500">
                All postcodes
              </span>
            </div>
            <span className="rounded-full bg-purple-100 px-2 py-1 text-sm text-purple-800">
              Non-direct
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <TradeTerm term="Price tier" />
            <TradeTerm term="Product catalogue" />
          </div>
        </div>
      </div>
    </Card>
  );
};

const CustomerZoneItem = ({
  customerZone,
}: {
  customerZone: CustomerZoneListResponseType[number];
}) => {
  const navigate = useNavigate();

  const uniqueDirectPostcodes = useUniqueDirectPostcodes();

  const showAlert =
    !customerZone.AvailabilityTierId || !customerZone.PriceTierId;

  const missingAssignments = [
    ...(!customerZone.AvailabilityTierId ? ["product catalogue"] : []),
    ...(!customerZone.PriceTierId ? ["price tier"] : []),
  ];

  const unbundlingRelease2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  return (
    <Card
      className={twMerge(
        "bg-white",
        "flex w-full cursor-pointer overflow-hidden border border-zinc-300 hover:bg-zinc-50",
        showAlert && "border-blue-300",
      )}
      onClick={() =>
        navigate(ROUTES.CUSTOMER_ZONE.href({ customerZoneId: customerZone.id }))
      }
    >
      <div className="flex w-full flex-col">
        {showAlert && (
          <Alert
            title={`Assign a ${missingAssignments.join(" and ")} to your Nationwide customer zone`}
            message={`Your ${customerZone.name} customers won't see the correct products and pricing until you have both a product catalogue and a price tier assigned`}
            type={"information"}
            className="rounded-b-none border-0"
          />
        )}
        <div className="flex w-full flex-col gap-4 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col">
              <span className="font-semibold">{customerZone.name}</span>
              <span className="text-sm text-zinc-500">
                {customerZone.type === "thirdParty"
                  ? "All postcodes"
                  : uniqueDirectPostcodes.textList}
              </span>
            </div>
            <span
              className={twMerge(
                "rounded-full px-2 py-1 text-sm",
                customerZone.type === "direct"
                  ? "bg-purple-800 text-white"
                  : "bg-purple-100 text-purple-800",
              )}
            >
              {customerZone.type === "direct" ? "Direct" : "Non-direct"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <TradeTerm
              term="Price tier"
              name={customerZone.PriceTier?.name}
              notSet={!customerZone.PriceTierId}
            />
            <TradeTerm
              term="Product catalogue"
              name={customerZone.AvailabilityTier?.name}
              notSet={!customerZone.AvailabilityTierId}
            />
            {unbundlingRelease2aEnabled && !unbundlingEnabled && (
              <TradeTerm
                term="Order rules"
                name={customerZone.OrderRules?.name}
                notSet={!customerZone.OrderRulesId}
              />
            )}
            {unbundlingEnabled && (
              <>
                {customerZone.type === "direct" && (
                  <TradeTerm
                    term="Direct"
                    name={
                      customerZone.DrayDeliveryMethod?.name ??
                      capitalize(
                        customerZone.DrayDeliveryMethod?.fulfillmentType ?? "",
                      )
                    }
                    notSet={!customerZone.DrayDeliveryMethodId}
                  />
                )}
                <TradeTerm
                  term="Courier"
                  name={
                    customerZone.CourierDeliveryMethod?.name ??
                    capitalize(
                      customerZone.CourierDeliveryMethod?.fulfillmentType ?? "",
                    )
                  }
                  notSet={!customerZone.CourierDeliveryMethodId}
                />
                <TradeTerm
                  term="Pallet"
                  name={
                    customerZone.PalletDeliveryMethod?.name ??
                    capitalize(
                      customerZone.PalletDeliveryMethod?.fulfillmentType ?? "",
                    )
                  }
                  notSet={!customerZone.PalletDeliveryMethodId}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const SellarDistributionCustomerZoneItem = ({
  customerZone,
}: {
  customerZone: CustomerZoneListResponseType[number];
}) => {
  const navigate = useNavigate();

  const sellarDistributionPostcodeList =
    useSellarDistributionAvailabilityDeliveryPostcodeList({
      query: [
        {
          type: "SupplierId",
          value: customerZone.SupplierId,
          modifier: "EQUALS",
        },
      ],
    });

  const postcodes =
    sellarDistributionPostcodeList.data?.pages.flatMap((page) =>
      page.map((postcode) => postcode.postcodeComponent),
    ) ?? [];

  const uniqueDirectPostcodes = useUniqueDirectPostcodes(postcodes);

  const showAlert =
    !customerZone.AvailabilityTierId || !customerZone.PriceTierId;

  const missingAssignments = [
    ...(!customerZone.AvailabilityTierId ? ["product catalogue"] : []),
    ...(!customerZone.PriceTierId ? ["price list"] : []),
  ];

  return (
    <Card
      className={twMerge(
        "bg-white",
        "flex w-full cursor-pointer overflow-hidden border border-zinc-300 hover:bg-zinc-50",
        showAlert && "border-blue-300",
      )}
      onClick={() =>
        navigate(ROUTES.CUSTOMER_ZONE.href({ customerZoneId: customerZone.id }))
      }
    >
      <div className="flex w-full flex-col">
        {showAlert && (
          <Alert
            title={`Assign a ${missingAssignments.join(" and ")} to your Sellar Distribution customer zone`}
            message={`Your ${customerZone.name} customers won't see the correct products and pricing until you have both a product catalogue and a price tier assigned`}
            type={"information"}
            className="rounded-b-none border-0"
          />
        )}
        <div className="flex w-full flex-col gap-4 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col">
              <span className="font-semibold">{customerZone.name}</span>
              <span className="text-sm text-zinc-500">
                {uniqueDirectPostcodes.textList}
              </span>
            </div>
            <span className="rounded-full bg-purple-800 px-2 py-1 text-sm text-white">
              Sellar Distribution
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <TradeTerm
              term="Price tier"
              name={customerZone.PriceTier?.name}
              notSet={!customerZone.PriceTierId}
            />
            <TradeTerm
              term="Product catalogue"
              name={customerZone.AvailabilityTier?.name}
              notSet={!customerZone.AvailabilityTierId}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

const TradeTerm = ({
  term,
  name,
  notSet = false,
}: {
  term:
    | "Price tier"
    | "Product catalogue"
    | "Order rules"
    | "Direct"
    | "Courier"
    | "Pallet";
  name?: string;
  notSet?: boolean;
}) => {
  const icon = (): IconType => {
    switch (term) {
      case "Price tier":
        return "TAG_OUTLINE";
      case "Product catalogue":
        return "BOOK_OUTLINE";
      case "Order rules":
        return "CLIPBOARD_CHECK_OUTLINE";
      case "Direct":
        return "TRUCK_DELIVERY";
      case "Courier":
        return "PACKAGE_VARIANT_CLOSED";
      case "Pallet":
        return "PALLET";
    }
  };

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const tradeTerm =
    unbundlingEnabled && term === "Price tier" ? "Price list" : term;

  if (notSet)
    return (
      <div className="flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-100 px-2 py-1 text-sm">
        <span className="flex items-center gap-1 font-semibold text-blue-800">
          <Icon icon="INFORMATION" /> No {term.toLowerCase()} set
        </span>
      </div>
    );

  return (
    <div className="flex items-center gap-1 rounded-lg border border-gray-300 bg-gray-100 px-2 py-1 text-sm">
      <span className="flex items-center gap-1 font-semibold">
        <Icon icon={icon()} />{" "}
        {name ? `${tradeTerm}:` : `No ${tradeTerm.toLowerCase()} set`}
      </span>
      {name}
    </div>
  );
};

const StorefrontPreviewButton = () => {
  const widthLessThan640 = useMediaQuery("(max-width: 640px)");
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    selectedCustomerZone,
    productListQuery,
    tab,
    onChangeTab,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setSelectedCustomerZone,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER_ZONE,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <Button
          onClick={() => setOpenStorefrontPreviewer(true)}
          color="secondary"
          className="p-2"
        >
          <Icon icon="STOREFRONT_OUTLINE" />
          {!widthLessThan640 && <span>Preview</span>}
        </Button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
